import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { 
  FaTrash, 
  FaPlus, 
  FaCamera, 
  FaServer, 
  FaExclamationTriangle, 
  FaSync,
  FaUndo,
  FaTimes,
  FaCheck,
  FaCheckCircle,
  FaInfoCircle,
  FaChevronLeft,
  FaChevronRight,
  FaArrowLeft,
  FaImage,
  FaChevronDown,
  FaCopy,
  FaSearch,
  FaSort
} from 'react-icons/fa';
import { Camera } from 'lucide-react';

import { useNavigate } from 'react-router-dom';
import { api } from '../../services/api';
import CreateSnapshotModal from './CreateSnapshotModal';
import RestoreSnapshotModal from './RestoreSnapshotModal';
import Sidebar from '../../components/layout/Sidebar';
import { useNotification } from '../../Notifications';

import { useResponsive } from '../../components/vm/useResponsive';
import { useAuth } from '../../components/auth/AuthContext';

import '../../styling/barrack.css';
import * as Sentry from "@sentry/react";

// Sentry logging helpers
const logError = (message, context = {}, error = null) => {
  if (error) {
    Sentry.captureException(error, { 
      tags: { section: 'snapshot-page' },
      extra: { ...context, message }
    });
  } else {
    Sentry.captureMessage(message, { 
      level: 'error',
      tags: { section: 'snapshot-page' },
      extra: context 
    });
  }
};

const logInfo = (message, context = {}) => {
  Sentry.addBreadcrumb({
    message,
    category: 'snapshot-page',
    level: 'info',
    data: context
  });
};

const logUserAction = (action, context = {}) => {
  Sentry.addBreadcrumb({
    message: `User action: ${action}`,
    category: 'user-interaction',
    level: 'info',
    data: context
  });
};

const logPerformance = (operation, duration, context = {}) => {
  Sentry.addBreadcrumb({
    message: `Performance: ${operation} took ${duration}ms`,
    category: 'performance',
    level: 'info',
    data: { ...context, duration }
  });
};

// Security: Safe error messages mapping
const ERROR_MESSAGES = {
  'AUTH_ERROR': 'Please log in again to continue',
  'SESSION_EXPIRED': 'Your session has expired. Please log in again.',
  'SNAPSHOT_LIST_FAILED': 'Unable to load snapshots. Please refresh the page.',
  'SNAPSHOT_DETAILS_FAILED': 'Unable to load snapshot details. Please try again.',
  'SNAPSHOT_CREATE_FAILED': 'Unable to create snapshot. Please try again.',
  'SNAPSHOT_DELETE_FAILED': 'Unable to delete snapshot. Please try again.',
  'SNAPSHOT_RESTORE_FAILED': 'Unable to restore snapshot. Please try again.',
  'VM_NOT_ACTIVE': 'VM must be active for snapshot operations',
  'RATE_LIMIT_EXCEEDED': 'API limit reached. Try again after 60 seconds',
  'SNAPSHOT_ALREADY_EXISTS': 'A snapshot with this name already exists',
  'USER_NOT_FOUND': 'Please refresh and try again',
  'SERVICE_CONFIG_ERROR': 'Service temporarily unavailable',
  'INVALID_ID_FORMAT': 'Invalid request. Please try again',
  'INVALID_SNAPSHOT_ID_FORMAT': 'Invalid snapshot ID format',
  'SNAPSHOT_STATUS_NOT_DELETABLE': 'Snapshot cannot be deleted in current status',
  'INVALID_SNAPSHOT_PARAMETERS': 'Please check your input and try again',
  'INVALID_DATA': 'Invalid input data',
  'INVALID_REQUEST': 'Invalid request format',
  'DB_ERROR': 'Service temporarily unavailable',
  'INCOMPLETE_PROFILE': 'Please complete your profile before creating snapshots',
  'INSUFFICIENT_CREDITS': 'Insufficient account balance. Please add funds to continue',
  'VM_UNAVAILABLE': 'Selected VM is unavailable or you do not have permission to access it',
  'SNAPSHOT_NOT_FOUND': 'Snapshot not found or you do not have permission to access it',
  'UPSTREAM_TIMEOUT': 'The operation is taking longer than expected. Please try again.',
'ECONNABORTED': 'Request timed out. Please try again.',
'ETIMEDOUT': 'Request timed out. Please try again.',
  'INVALID_CONTRACT_ID': 'Invalid contract selection. Please try again',
  'NETWORK_ERROR': 'Network connection failed. Please check your internet connection and try again.',
  'SERVICE_UNAVAILABLE': 'Service temporarily unavailable. Please try again later.',
  // Network Errors (add these)
'OFFLINE': "You're offline. Check your connection.",
'TIMEOUT': "Request timed out. Please try again.",
'NO_RESPONSE': "No response from server. Please try again.",
'UNKNOWN_NETWORK_ERROR': "Something went wrong. Please try again.",
'TOO_MANY_REQUESTS': "Too many requests. Please wait before trying again.",
'OFFLINE': "You're offline. Check your connection.",
'TIMEOUT': "Request timed out. Please try again.",
'SERVER_UNREACHABLE': "Network connection failed. Please check your internet connection and try again.",
'NO_RESPONSE': "No response from server. Please try again.",
'UNKNOWN_NETWORK_ERROR': "Something went wrong. Please try again.",
'TOO_MANY_REQUESTS': "Too many requests. Please wait before trying again."
};

// Security: Input sanitization function
const sanitizeText = (text) => {
  if (!text || typeof text !== 'string') return '';
  return text
    .replace(/[<>&"']/g, (char) => {
      const entities = { '<': '&lt;', '>': '&gt;', '&': '&amp;', '"': '&quot;', "'": '&#x27;' };
      return entities[char];
    })
    .substring(0, 1000);
};

// Security: Input validation functions
const validateSnapshotName = (name) => {
  if (!name || typeof name !== 'string') {
    return { valid: false, error: 'Name is required' };
  }
  if (name.length < 2) {
    return { valid: false, error: 'Name must be at least 2 characters' };
  }
  if (name.length > 30) {
    return { valid: false, error: 'Name must be 30 characters or less' };
  }
  if (!/^[a-zA-Z0-9\s\-_]+$/.test(name)) {
    return { 
      valid: false, 
      error: 'Use only letters, numbers, spaces, hyphens, and underscores.' 
    };
  }
  if (/^[-_]|[-_]$/.test(name)) {
    return {
      valid: false,
      error: 'Name cannot start or end with hyphen or underscore'
    };
  }
  return { valid: true };
};

const validateSnapshotDescription = (description) => {
  if (description && typeof description === 'string' && description.length > 500) {
    return { valid: false, error: 'Description must be 500 characters or less' };
  }
  return { valid: true };
};

// Security: Safe error message function
// Security: Safe error message function
const getSafeErrorMessage = (error) => {
  if (!error || typeof error !== 'object') {
    return 'Something went wrong. Please try again.';
  }
  
  // Position 1: Check error.code against ERROR_MESSAGES map
  if (error.code && ERROR_MESSAGES[error.code]) {
    return ERROR_MESSAGES[error.code];
  }
  
  // Position 2: Server errors (500+)
  if (error.status >= 500) {
    return ERROR_MESSAGES['SERVICE_UNAVAILABLE'];
  }
  
  // Position 3: Rate limit
  if (error.status === 429 || error.code === 'RATE_LIMIT_EXCEEDED') {
    return ERROR_MESSAGES['RATE_LIMIT_EXCEEDED'];
  }
  
  // Position 4: Auth error
  if (error.status === 401) {
    return ERROR_MESSAGES['SESSION_EXPIRED'];
  }
  
  // Position 5: Insufficient credits
  if (error.status === 402) {
    return ERROR_MESSAGES['INSUFFICIENT_CREDITS'];
  }
  
  // Position 6: Use error.error (user-friendly message from interceptor)
  if (error.error && typeof error.error === 'string') {
    return error.error;
  }
  
  // Position 7: Fallback
  return 'Something went wrong. Please try again.';
};// Security: Validate error object structure
const validateErrorObject = (error) => {
  if (!error || typeof error !== 'object') return false;
  return true;
};

// Performance: Debounce function
const debounce = (func, delay) => {
  let timeoutId;
  return (...args) => {
    clearTimeout(timeoutId);
    timeoutId = setTimeout(() => func.apply(null, args), delay);
  };
};

const isAdvancedRegion = (vm) => {
  const advancedRegions = [
    "FINLAND-1", "FINLAND-2", "FINLAND-3",
    "FIN-01", "FIN-02", "FIN-03", "ICE-01"
  ];
  return advancedRegions.includes(vm?.region);
};

// Group pagination items per page
const GROUP_ITEMS_PER_PAGE = 15;

// Group Pagination Component
const GroupPagination = ({ currentPage, totalPages, onPageChange, totalItems }) => {
  if (totalPages <= 1) return null;
  
  const startItem = (currentPage - 1) * GROUP_ITEMS_PER_PAGE + 1;
  const endItem = Math.min(currentPage * GROUP_ITEMS_PER_PAGE, totalItems);
  
  return (
    <div style={{
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      padding: '12px 16px',
      background: '#141414',
      borderTop: '1px solid #2a2a2a'
    }}>
      <span style={{ fontSize: '12px', color: '#6b7280' }}>
        Showing {startItem}-{endItem} of {totalItems}
      </span>
      <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
        <button
          onClick={() => onPageChange(currentPage - 1)}
          disabled={currentPage === 1}
          style={{
            width: '28px',
            height: '28px',
            borderRadius: '4px',
            border: 'none',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
            background: currentPage === 1 ? '#1a1a1a' : '#333',
            color: currentPage === 1 ? '#444' : '#fff'
          }}
        >
          <FaChevronLeft size={10} />
        </button>
        
        {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
          <button
            key={page}
            onClick={() => onPageChange(page)}
            style={{
              minWidth: '28px',
              height: '28px',
              padding: '0 6px',
              borderRadius: '4px',
              border: 'none',
              cursor: 'pointer',
              fontSize: '12px',
              background: currentPage === page ? '#e0eacc' : '#252525',
              color: currentPage === page ? '#000' : '#9ca3af',
              fontWeight: currentPage === page ? '600' : '400'
            }}
          >
            {page}
          </button>
        ))}
        
        <button
          onClick={() => onPageChange(currentPage + 1)}
          disabled={currentPage === totalPages}
          style={{
            width: '28px',
            height: '28px',
            borderRadius: '4px',
            border: 'none',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: currentPage === totalPages ? 'not-allowed' : 'pointer',
            background: currentPage === totalPages ? '#1a1a1a' : '#333',
            color: currentPage === totalPages ? '#444' : '#fff'
          }}
        >
          <FaChevronRight size={10} />
        </button>
      </div>
    </div>
  );
};

// Delete Confirmation Modal Component - Updated to match standard pattern
const DeleteConfirmationModal = ({ isOpen, onClose, onConfirm, itemName, itemType = "snapshot", isDeleting }) => {
  if (!isOpen) return null;
  
  const capitalizedType = itemType.charAt(0).toUpperCase() + itemType.slice(1);
 
  return (
    <div style={{
      position: 'fixed',
      inset: 0,
      backgroundColor: 'rgba(0,0,0,0.75)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 50,
      padding: '16px'
    }}>
      <div style={{
        backgroundColor: '#121212',
        border: '1px solid #333333',
        borderRadius: '12px',
        width: '100%',
        maxWidth: '420px',
        boxShadow: '0 4px 20px rgba(0,0,0,0.5)',
        overflow: 'hidden'
      }}>
        {/* Header */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: '16px 20px',
          borderBottom: '1px solid #2a2a2a'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="#ff7777">
              <path d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z"/>
            </svg>
            <h3 style={{
              fontWeight: '600',
              color: '#fff',
              margin: 0,
              fontSize: '16px'
            }}>
              Delete {capitalizedType}
            </h3>
          </div>
          <button
            onClick={onClose}
            disabled={isDeleting}
            style={{
              padding: '8px',
              borderRadius: '6px',
              backgroundColor: 'transparent',
              border: 'none',
              color: '#6b7280',
              cursor: isDeleting ? 'not-allowed' : 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
              <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/>
            </svg>
          </button>
        </div>
        
        {/* Content */}
        <div style={{ padding: '20px' }}>
          <p style={{
            color: '#9ca3af',
            marginBottom: '16px',
            marginTop: 0,
            fontSize: '14px',
            lineHeight: '1.6'
          }}>
            Are you sure you want to delete this {itemType}?
          </p>
          
          {/* Item Name Box */}
          <div style={{
            backgroundColor: '#1a1a1a',
            border: '1px solid #2a2a2a',
            borderRadius: '8px',
            padding: '12px 14px',
            marginBottom: '16px'
          }}>
            <span style={{
              color: '#6b7280',
              fontSize: '11px',
              display: 'block',
              marginBottom: '4px',
              textTransform: 'uppercase',
              letterSpacing: '0.05em'
            }}>
              {capitalizedType} Name
            </span>
            <span style={{
              color: '#e0eacc',
              fontSize: '14px',
              fontWeight: '500'
            }}>
              {sanitizeText(itemName)}
            </span>
          </div>
          
          {/* Warning Box */}
          <div style={{
            backgroundColor: 'rgba(255,119,119,0.05)',
            border: '1px solid rgba(255,119,119,0.2)',
            borderRadius: '8px',
            padding: '10px 12px',
            marginBottom: '20px',
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="#ff8080" style={{ flexShrink: 0 }}>
              <path d="M1 21h22L12 2 1 21zm12-3h-2v-2h2v2zm0-4h-2v-4h2v4z"/>
            </svg>
            <span style={{ color: '#ff8080', fontSize: '12px' }}>
              This action cannot be undone.
            </span>
          </div>
          
          {/* Delete Button */}
          <button
            onClick={onConfirm}
            disabled={isDeleting}
            style={{
              width: '100%',
              backgroundColor: isDeleting ? '#4a3030' : 'rgba(255,100,100,0.15)',
              border: '1px solid rgba(255,119,119,0.4)',
              color: '#ff7777',
              borderRadius: '8px',
              fontWeight: '500',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '12px 20px',
              fontSize: '14px',
              cursor: isDeleting ? 'not-allowed' : 'pointer',
              gap: '8px'
            }}
          >
            {isDeleting ? (
              <>
                <div style={{
                  width: '16px',
                  height: '16px',
                  border: '2px solid transparent',
                  borderTopColor: '#ff7777',
                  borderRadius: '50%',
                  animation: 'spin 1s linear infinite'
                }}></div>
                Deleting...
              </>
            ) : (
              <>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z"/>
                </svg>
                Delete {capitalizedType}
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

const SnapshotPage = () => {
  const { isMobile, isTablet } = useResponsive();
  const notification = useNotification();
  const { handleAuthError } = useAuth();

  // Performance: Initial load tracking
  const [initialDataLoaded, setInitialDataLoaded] = useState(false);
  
  // Performance: Granular loading states
  const [loadingStates, setLoadingStates] = useState({
    snapshots: false,
    vms: false,
    creating: false,
    deleting: new Set(),
    restoring: new Set()
  });
  
  // State for snapshots
  const [snapshots, setSnapshots] = useState([]);
  const [vms, setVms] = useState([]);
  const [error, setError] = useState(null);
  const [selectedSnapshot, setSelectedSnapshot] = useState(null);
  const [regions] = useState(['CANADA-1']);
  const [selectedRegion, setSelectedRegion] = useState('CANADA-1');
  const [isRegionDropdownOpen, setIsRegionDropdownOpen] = useState(false);
  const [statusMessage, setStatusMessage] = useState(null);
  const [deleteModal, setDeleteModal] = useState({ open: false, snapshot: null });
  // Profile completeness state
  const [isProfileComplete, setIsProfileComplete] = useState(true);
  
  // Snapshot-image relationship state
  const [snapshotImageStatus, setSnapshotImageStatus] = useState(new Map());


  
  // Modal states
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [restoreModalOpen, setRestoreModalOpen] = useState(false);
  
  // NEW: Grouped list view states
  const [collapsedGroups, setCollapsedGroups] = useState({});
  const [expandedSnapshotId, setExpandedSnapshotId] = useState(null);
  const [groupPages, setGroupPages] = useState({ 'In Progress': 1, Available: 1 });
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState('name');
  const [sortDropdownOpen, setSortDropdownOpen] = useState(false);
  const [copiedId, setCopiedId] = useState(null);

  const regionDropdownRef = useRef(null);
  
  
  const navigate = useNavigate();

  // Sort options for dropdown
  const sortOptions = [
    { id: 'name', label: 'Name' },
    { id: 'size', label: 'Size' },
    { id: 'created', label: 'Date' },
  ];

  // Copy to clipboard helper
  const copyToClipboard = useCallback((id) => {
    navigator.clipboard.writeText(id);
    setCopiedId(id);
    setTimeout(() => setCopiedId(null), 2000);
    logUserAction('copy_snapshot_id', { snapshotId: id });
  }, []);

  // Toggle group collapse
  const toggleGroup = useCallback((groupName) => {
    setCollapsedGroups(prev => ({ 
      ...prev, 
      [groupName]: !prev[groupName] 
    }));
    logUserAction('toggle_snapshot_group', { 
      groupName, 
      collapsed: !collapsedGroups[groupName] 
    });
  }, [collapsedGroups]);

  // Handle group page change
  const handleGroupPageChange = useCallback((groupName, page) => {
    setGroupPages(prev => ({ ...prev, [groupName]: page }));
    logUserAction('group_page_change', { groupName, page });
  }, []);

  // Check profile completeness
  const checkProfileCompleteness = useCallback(async () => {
    try {
      const response = await api.getUserProfile();
      if (response && response.success && response.profile) {
        setIsProfileComplete(response.profile.is_profile_complete || false);
      }
    } catch (error) {
      logError('Failed to check profile completeness', { error: error.message });
    }
  }, []);
  
  // Check profile on component mount
  useEffect(() => {
    checkProfileCompleteness();
  }, [checkProfileCompleteness]);

  // Set user context for Sentry
  useEffect(() => {
    Sentry.setContext("snapshot-page", {
      snapshotCount: snapshots.length,
      selectedRegion,
      vmCount: vms.length,
      isMobile,
      isTablet
    });
  }, [snapshots.length, selectedRegion, vms.length, isMobile, isTablet]);
 
  // Performance: Memoized filtered snapshots
  const filteredSnapshots = useMemo(() => {
    if (!snapshots || !Array.isArray(snapshots)) return [];
    
    let result = snapshots.filter(snapshot => {
      if (!snapshot) return false;
      
      if (snapshot.region) {
        return snapshot.region.startsWith(selectedRegion.split('-')[0]);
      }
      if (snapshot.vm_environment && snapshot.vm_environment.region) {
        return snapshot.vm_environment.region.startsWith(selectedRegion.split('-')[0]);
      }
      return false;
    });

    // Apply search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      result = result.filter(s => 
        (s.name && s.name.toLowerCase().includes(query)) ||
        (s.snapshot_id && s.snapshot_id.toLowerCase().includes(query)) ||
        (s.id && s.id.toString().toLowerCase().includes(query))
      );
    }

    // Apply sorting
    result = [...result].sort((a, b) => {
      if (sortBy === 'name') {
        return (a.name || '').localeCompare(b.name || '');
      }
      if (sortBy === 'size') {
        return (b.size || 0) - (a.size || 0);
      }
      if (sortBy === 'created') {
        const dateA = new Date(a.created_at || 0);
        const dateB = new Date(b.created_at || 0);
        return dateB - dateA;
      }
      return 0;
    });

    logInfo('Snapshots filtered', {
      selectedRegion,
      totalSnapshots: snapshots.length,
      filteredCount: result.length,
      searchQuery,
      sortBy
    });

    return result;
  }, [snapshots, selectedRegion, searchQuery, sortBy]);

  // NEW: Group snapshots by status
  const groupedSnapshots = useMemo(() => {
    const groups = { 'In Progress': [], Available: [] };
    
    filteredSnapshots.forEach(snapshot => {
      const status = snapshot.status;
      if (status === 'available' || status === 'SUCCESS') {
        groups.Available.push(snapshot);
      } else {
        groups['In Progress'].push(snapshot);
      }
    });
    
    logInfo('Snapshots grouped by status', {
      inProgressCount: groups['In Progress'].length,
      availableCount: groups.Available.length,
      totalFiltered: filteredSnapshots.length
    });
    
    return groups;
  }, [filteredSnapshots]);

  // NEW: Paginated snapshots per group
  const getPaginatedGroupSnapshots = useCallback((groupName) => {
    const groupSnaps = groupedSnapshots[groupName] || [];
    const currentPage = groupPages[groupName] || 1;
    const startIndex = (currentPage - 1) * GROUP_ITEMS_PER_PAGE;
    const endIndex = startIndex + GROUP_ITEMS_PER_PAGE;
    return groupSnaps.slice(startIndex, endIndex);
  }, [groupedSnapshots, groupPages]);

  // NEW: Get total pages for a group
  const getGroupTotalPages = useCallback((groupName) => {
    const groupSnaps = groupedSnapshots[groupName] || [];
    return Math.ceil(groupSnaps.length / GROUP_ITEMS_PER_PAGE);
  }, [groupedSnapshots]);

  // Check image status when snapshots change
const checkAllSnapshotsImageStatus = useCallback(async () => {
  const currentSnapshots = [
    ...getPaginatedGroupSnapshots('Available'),
    ...getPaginatedGroupSnapshots('In Progress')
  ];
    
    if (currentSnapshots.length === 0) return;
    
    try {
      const snapshotIds = currentSnapshots.map(snapshot => snapshot.snapshot_id).filter(id => id != null);
      
      logInfo('Checking batch snapshot image status', {
        snapshotCount: snapshotIds.length,
        snapshotIds
      });
      
      const response = await api.getBatchSnapshotImageRelationships(snapshotIds);
      
  if (response.success && response.relationships) {
  const newStatusMap = new Map();
  
  Object.entries(response.relationships).forEach(([snapshotId, relationship]) => {
    newStatusMap.set(snapshotId, {
      hasImage: relationship.has_image,
      image: relationship.image
    });
  });
  
  console.log('Setting snapshotImageStatus:', Object.fromEntries(newStatusMap));  // Add this
  setSnapshotImageStatus(newStatusMap);
        
        logInfo('Batch snapshot image status updated', {
          snapshotCount: snapshotIds.length,
          foundImages: Object.values(response.relationships).filter(r => r.has_image).length
        });
      }
    } catch (error) {
      logError('Error checking batch image status for snapshots', {
        error: error.message
      }, error);
    }
  }, [getPaginatedGroupSnapshots]);

  useEffect(() => {
    checkAllSnapshotsImageStatus();
  }, [filteredSnapshots, groupPages, checkAllSnapshotsImageStatus]);
  
  // Performance: Memoized filtered VMs
  const filteredVms = useMemo(() => {
    if (!vms || !Array.isArray(vms)) return [];
    
    const filtered = vms.filter(vm => vm && vm.region && vm.region.startsWith(selectedRegion.split('-')[0]));

    logInfo('VMs filtered by region', {
      selectedRegion,
      totalVMs: vms.length,
      filteredCount: filtered.length
    });

    return filtered;
  }, [vms, selectedRegion]);

const clearError = useCallback(() => {
  setError(null);
}, []);
  
const debouncedFetchSnapshots = useMemo(
  () => debounce(async () => {
    const startTime = performance.now();
    
    logInfo('Initiating snapshot fetch request', {
      selectedRegion
    });
    
    setLoadingStates(prev => ({ ...prev, snapshots: true }));
    setError(null);
    
    try {
      const response = await api.getSnapshots();
      const endTime = performance.now();
      const duration = endTime - startTime;
      
      if (response && validateErrorObject(response)) {
        if (response.success) {
          const snapshotData = response.snapshots || [];
          setSnapshots(Array.isArray(snapshotData) ? snapshotData : []);
          
          if (!initialDataLoaded) setInitialDataLoaded(true);

          logPerformance('fetch_snapshots', duration, {
            snapshotCount: snapshotData.length,
            selectedRegion,
            success: true
          });

          logInfo('Snapshots loaded successfully', {
            count: snapshotData.length,
            duration: `${duration.toFixed(2)}ms`
          });
        } else {
          const safeMessage = getSafeErrorMessage(response);
          setError(safeMessage);
          setSnapshots([]);

          logError('Failed to fetch snapshots', {
            response: response || 'No response',
            selectedRegion,
            duration: `${duration.toFixed(2)}ms`
          });
        }
      } else {
        setError('Invalid response format');
        setSnapshots([]);

        logError('Invalid snapshot response format', {
          response: response || 'No response',
          selectedRegion,
          duration: `${duration.toFixed(2)}ms`
        });
      }
    } catch (err) {
      const endTime = performance.now();
      const duration = endTime - startTime;
      
      // Position 1: Authentication error
      if (err.status === 401 || err.code === 'AUTH_ERROR') {
        handleAuthError();
        setLoadingStates(prev => ({ ...prev, snapshots: false }));
        return;
      }
      
      // Position 2: Rate limit
      if (err.status === 429 || err.code === 'RATE_LIMIT_EXCEEDED') {
        setError(ERROR_MESSAGES['RATE_LIMIT_EXCEEDED']);
        setSnapshots([]);
        logError('Snapshots fetch rate limited', { 
          status: err.status,
          code: err.code,
          duration: `${duration.toFixed(2)}ms` 
        }, err);
        setLoadingStates(prev => ({ ...prev, snapshots: false }));
        return;
      }
      
      // Position 3: Server errors (500+)
      if (err.status >= 500 || err.code === 'SERVER_ERROR' || err.code === 'SERVICE_UNAVAILABLE') {
        setError(ERROR_MESSAGES['SERVICE_UNAVAILABLE']);
        setSnapshots([]);
        logError('Snapshots fetch server error', { 
          status: err.status,
          code: err.code,
          duration: `${duration.toFixed(2)}ms` 
        }, err);
        setLoadingStates(prev => ({ ...prev, snapshots: false }));
        return;
      }
      
      // Position 4: Network errors (no status means network issue)
      if (!err.status) {
        const errorMessage = ERROR_MESSAGES[err.code] || err.error || ERROR_MESSAGES['NETWORK_ERROR'];
        setError(errorMessage);
        setSnapshots([]);
        logError('Network error in fetchSnapshots', { 
          code: err.code,
          online: navigator.onLine,
          duration: `${duration.toFixed(2)}ms`
        }, err);
        setLoadingStates(prev => ({ ...prev, snapshots: false }));
        return;
      }
      
      // Position 5: Known error codes
      if (err.code && ERROR_MESSAGES[err.code]) {
        setError(ERROR_MESSAGES[err.code]);
        setSnapshots([]);
        logError('Snapshots fetch error with code', {
          code: err.code,
          duration: `${duration.toFixed(2)}ms`
        }, err);
        setLoadingStates(prev => ({ ...prev, snapshots: false }));
        return;
      }
      
      // Position 6: Use err.error (user-friendly message from interceptor)
      if (err.error) {
        setError(err.error);
        setSnapshots([]);
        logError('Snapshots fetch error', {
          error: err.error,
          duration: `${duration.toFixed(2)}ms`
        }, err);
        setLoadingStates(prev => ({ ...prev, snapshots: false }));
        return;
      }
      
      // Position 7: Fallback
      const safeMessage = getSafeErrorMessage(err);
      setError(safeMessage);
      setSnapshots([]);

      logError('Error fetching snapshots', {
        errorName: err.name,
        selectedRegion,
        duration: `${duration.toFixed(2)}ms`
      }, err);
    } finally {
      setLoadingStates(prev => ({ ...prev, snapshots: false }));
    }
  }, 300),
  [selectedRegion, handleAuthError, initialDataLoaded]
);

const debouncedFetchVms = useMemo(
  () => debounce(async () => {
    const startTime = performance.now();
    
    logInfo('Initiating VM fetch request for snapshots', {
      selectedRegion
    });

    setLoadingStates(prev => ({ ...prev, vms: true }));
    
    try {
      const response = await api.listVmsForSnapshot();
      const endTime = performance.now();
      const duration = endTime - startTime;
      
      if (response && validateErrorObject(response)) {
        if (response.success) {
          const vmData = response.instances || [];
          setVms(Array.isArray(vmData) ? vmData : []);

          logPerformance('fetch_vms_for_snapshots', duration, {
            vmCount: vmData.length,
            selectedRegion,
            success: true
          });

          logInfo('VMs for snapshots loaded successfully', {
            count: vmData.length,
            duration: `${duration.toFixed(2)}ms`
          });
        } else {
          const safeMessage = getSafeErrorMessage(response);
          setError(safeMessage);

          logError('Failed to fetch VMs for snapshots', {
            response: response || 'No response',
            selectedRegion,
            duration: `${duration.toFixed(2)}ms`
          });
        }
      }
    } catch (err) {
      const endTime = performance.now();
      const duration = endTime - startTime;
      
      // Position 1: Authentication error
      if (err.status === 401 || err.code === 'AUTH_ERROR') {
        handleAuthError();
        setLoadingStates(prev => ({ ...prev, vms: false }));
        return;
      }
      
      // Position 2: Rate limit
      if (err.status === 429 || err.code === 'RATE_LIMIT_EXCEEDED') {
        setError(ERROR_MESSAGES['RATE_LIMIT_EXCEEDED']);
        logError('VMs fetch rate limited', { 
          status: err.status,
          code: err.code,
          duration: `${duration.toFixed(2)}ms` 
        }, err);
        setLoadingStates(prev => ({ ...prev, vms: false }));
        return;
      }
      
      // Position 3: Server errors (500+)
      if (err.status >= 500 || err.code === 'SERVER_ERROR' || err.code === 'SERVICE_UNAVAILABLE') {
        setError(ERROR_MESSAGES['SERVICE_UNAVAILABLE']);
        logError('VMs fetch server error', { 
          status: err.status,
          code: err.code,
          duration: `${duration.toFixed(2)}ms` 
        }, err);
        setLoadingStates(prev => ({ ...prev, vms: false }));
        return;
      }
      
      // Position 4: Network errors (no status means network issue)
      if (!err.status) {
        const errorMessage = ERROR_MESSAGES[err.code] || err.error || ERROR_MESSAGES['NETWORK_ERROR'];
        setError(errorMessage);
        logError('Network error in fetchVms', { 
          code: err.code,
          online: navigator.onLine,
          duration: `${duration.toFixed(2)}ms`
        }, err);
        setLoadingStates(prev => ({ ...prev, vms: false }));
        return;
      }
      
      // Position 5: Known error codes
      if (err.code && ERROR_MESSAGES[err.code]) {
        setError(ERROR_MESSAGES[err.code]);
        logError('VMs fetch error with code', {
          code: err.code,
          duration: `${duration.toFixed(2)}ms`
        }, err);
        setLoadingStates(prev => ({ ...prev, vms: false }));
        return;
      }
      
      // Position 6: Use err.error (user-friendly message from interceptor)
      if (err.error) {
        setError(err.error);
        logError('VMs fetch error', {
          error: err.error,
          duration: `${duration.toFixed(2)}ms`
        }, err);
        setLoadingStates(prev => ({ ...prev, vms: false }));
        return;
      }
      
      // Position 7: Fallback
      const safeMessage = getSafeErrorMessage(err);
      setError(safeMessage);

      logError('Error fetching VMs for snapshots', {
        errorName: err.name,
        selectedRegion,
        duration: `${duration.toFixed(2)}ms`
      }, err);
    } finally {
      setLoadingStates(prev => ({ ...prev, vms: false }));
    }
  }, 300),
  [selectedRegion, handleAuthError]
);


  // Performance: Parallel API calls
  const fetchAllData = useCallback(async () => {
    const startTime = performance.now();
    
    logInfo('Initiating parallel data fetch', {
      selectedRegion
    });

    await Promise.all([
      debouncedFetchSnapshots(),
      debouncedFetchVms()
    ]);

    const endTime = performance.now();
    const duration = endTime - startTime;

    logPerformance('fetch_all_snapshot_data', duration, {
      selectedRegion,
      success: true
    });

    logInfo('Parallel data fetch completed', {
      duration: `${duration.toFixed(2)}ms`,
      selectedRegion
    });
  }, [debouncedFetchSnapshots, debouncedFetchVms, selectedRegion]);

  useEffect(() => {
    logInfo('SnapshotPage component mounted', {
      isMobile,
      isTablet,
      selectedRegion
    });
    
    fetchAllData();
  }, [selectedRegion, fetchAllData, isMobile, isTablet]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (regionDropdownRef.current && !regionDropdownRef.current.contains(event.target)) {
        setIsRegionDropdownOpen(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);


  // Reset group pages when region or search changes
  useEffect(() => {
    setGroupPages({ 'In Progress': 1, Available: 1 });
    setExpandedSnapshotId(null);
  }, [selectedRegion, searchQuery, sortBy]);


const handleCreateSnapshot = useCallback(async (vmId, snapshotData) => {
  const startTime = performance.now();
  
  logUserAction('create_snapshot_initiated', {
    vmId,
    snapshotName: snapshotData.name,
    hasDescription: !!snapshotData.description
  });

  logInfo('Starting snapshot creation', {
    vmId,
    snapshotName: snapshotData.name,
    hasDescription: !!snapshotData.description
  });

  const nameValidation = validateSnapshotName(snapshotData.name);
  if (!nameValidation.valid) {
    setError(nameValidation.error);
    logError('Snapshot creation failed - invalid name', {
      vmId,
      snapshotName: snapshotData.name,
      validationError: nameValidation.error
    });
    return;
  }
  
  const descValidation = validateSnapshotDescription(snapshotData.description);
  if (!descValidation.valid) {
    setError(descValidation.error);
    logError('Snapshot creation failed - invalid description', {
      vmId,
      snapshotName: snapshotData.name,
      validationError: descValidation.error
    });
    return;
  }
  
  const latestVm = vms.find(v => v.instance_id === vmId);

  if (!latestVm) {
    setError('VM not found. It may have been deleted.');
    return;
  }

  const isAdvanced = isAdvancedRegion(latestVm);

  if (isAdvanced) {
    setError('Snapshots are only available for VMs in legacy regions');
    return;
  }

  const isVmRunning = ['ACTIVE', 'RUNNING'].includes(latestVm.status?.toUpperCase());
  if (!isVmRunning) {
    setError(`VM must be running to create snapshot (current: ${latestVm.status})`);
    return;
  }
  
  if (!vmId) {
    setError('VM selection is required');
    logError('Snapshot creation failed - no VM selected', {
      snapshotName: snapshotData.name
    });
    return;
  }
  
  try {
    setLoadingStates(prev => ({ ...prev, creating: true }));
    
    const sanitizedData = {
      ...snapshotData,
      name: sanitizeText(snapshotData.name),
      description: snapshotData.description ? sanitizeText(snapshotData.description) : '',
      is_image: snapshotData.is_image || false
    };
    
    const response = await api.createSnapshot(vmId, sanitizedData);
    const endTime = performance.now();
    const duration = endTime - startTime;
    
    if (response && validateErrorObject(response)) {
      if (response.success) {
        setCreateModalOpen(false);
        notification.success('Snapshot created successfully');
        debouncedFetchSnapshots();

        logPerformance('create_snapshot', duration, {
          vmId,
          snapshotName: sanitizedData.name,
          success: true
        });

        logInfo('Snapshot created successfully', {
          vmId,
          snapshotName: sanitizedData.name,
          snapshotId: response.snapshot_id || 'pending',
          duration: `${duration.toFixed(2)}ms`
        });

        logUserAction('create_snapshot_success', {
          vmId,
          snapshotName: sanitizedData.name,
          snapshotId: response.snapshot_id || 'pending'
        });
      } else {
        const safeMessage = getSafeErrorMessage(response);
        setError(safeMessage);

        logError('Failed to create snapshot', {
          vmId,
          snapshotName: sanitizedData.name,
          response: response || 'No response',
          duration: `${duration.toFixed(2)}ms`
        });

        logUserAction('create_snapshot_failed', {
          vmId,
          snapshotName: sanitizedData.name
        });
      }
    }
  } catch (err) {
    const endTime = performance.now();
    const duration = endTime - startTime;
    
    // Position 1: Authentication error
    if (err.status === 401 || err.code === 'AUTH_ERROR') {
      handleAuthError();
      setLoadingStates(prev => ({ ...prev, creating: false }));
      return;
    }
    
    // Position 2: Rate limit
    if (err.status === 429 || err.code === 'RATE_LIMIT_EXCEEDED') {
      setError(ERROR_MESSAGES['RATE_LIMIT_EXCEEDED']);
      logError('Create snapshot rate limited', { 
        status: err.status,
        code: err.code,
        duration: `${duration.toFixed(2)}ms` 
      }, err);
      setLoadingStates(prev => ({ ...prev, creating: false }));
      return;
    }
    
    // Position 3: Server errors (500+)
    if (err.status >= 500 || err.code === 'SERVER_ERROR' || err.code === 'SERVICE_UNAVAILABLE') {
      setError(ERROR_MESSAGES['SERVICE_UNAVAILABLE']);
      logError('Create snapshot server error', { 
        status: err.status,
        code: err.code,
        duration: `${duration.toFixed(2)}ms` 
      }, err);
      setLoadingStates(prev => ({ ...prev, creating: false }));
      return;
    }
    
    // Position 4: Network errors (no status means network issue)
    if (!err.status) {
      const errorMessage = ERROR_MESSAGES[err.code] || err.error || ERROR_MESSAGES['NETWORK_ERROR'];
      setError(errorMessage);
      logError('Network error in createSnapshot', { 
        code: err.code,
        online: navigator.onLine,
        duration: `${duration.toFixed(2)}ms`,
        vmId,
        snapshotName: snapshotData.name
      }, err);
      setLoadingStates(prev => ({ ...prev, creating: false }));
      return;
    }
    
    // Position 5: Known error codes
    if (err.code && ERROR_MESSAGES[err.code]) {
      setError(ERROR_MESSAGES[err.code]);
      logError('Create snapshot error with code', {
        code: err.code,
        duration: `${duration.toFixed(2)}ms`
      }, err);
      setLoadingStates(prev => ({ ...prev, creating: false }));
      return;
    }
    
    // Position 6: Use err.error (user-friendly message from interceptor)
    if (err.error) {
      setError(err.error);
      logError('Create snapshot error', {
        error: err.error,
        duration: `${duration.toFixed(2)}ms`
      }, err);
      setLoadingStates(prev => ({ ...prev, creating: false }));
      return;
    }
    
    // Position 7: Fallback
    const safeMessage = getSafeErrorMessage(err);
    setError(safeMessage);

    logError('Error creating snapshot', {
      vmId,
      snapshotName: snapshotData.name,
      errorName: err.name,
      duration: `${duration.toFixed(2)}ms`
    }, err);

    logUserAction('create_snapshot_error', {
      vmId,
      snapshotName: snapshotData.name
    });
  } finally {
    setLoadingStates(prev => ({ ...prev, creating: false }));
  }
}, [vms, debouncedFetchSnapshots, handleAuthError, notification]);


const handleDeleteClick = useCallback((snapshot) => {
    if (!snapshot) return;
    
    logUserAction('delete_snapshot_modal_opened', {
      snapshotId: snapshot.snapshot_id,
      snapshotName: snapshot.name
    });

    logInfo('Opening delete snapshot modal', {
      snapshotId: snapshot.snapshot_id,
      snapshotName: snapshot.name,
      snapshotStatus: snapshot.status
    });
    
    setDeleteModal({
      open: true,
      snapshot: snapshot
    });
  }, []);
  
const handleDeleteConfirm = useCallback(async () => {
  if (!deleteModal.snapshot) return;
  
  const startTime = performance.now();
  const snapshotId = deleteModal.snapshot.snapshot_id;
  const snapshotName = deleteModal.snapshot.name;
  
  logUserAction('delete_snapshot_confirmed', {
    snapshotId,
    snapshotName
  });

  logInfo('Starting snapshot deletion', {
    snapshotId,
    snapshotName
  });
  
  setLoadingStates(prev => ({ 
    ...prev, 
    deleting: new Set([...prev.deleting, snapshotId])
  }));
  
  try {
    const response = await api.deleteSnapshot(snapshotId);
    const endTime = performance.now();
    const duration = endTime - startTime;
    
    if (response && validateErrorObject(response)) {
      if (response.success) {
        setSnapshotImageStatus(prev => {
          const newMap = new Map(prev);
          newMap.delete(snapshotId);
          return newMap;
        });
        
        notification.success('Snapshot deleted successfully');
        debouncedFetchSnapshots();

        logPerformance('delete_snapshot', duration, {
          snapshotId,
          snapshotName,
          success: true
        });

        logInfo('Snapshot deleted successfully', {
          snapshotId,
          snapshotName,
          duration: `${duration.toFixed(2)}ms`,
          deletedImageInfo: response.deletedImageInfo
        });

        logUserAction('delete_snapshot_success', {
          snapshotId,
          snapshotName,
          deletedImageInfo: response.deletedImageInfo
        });
      } else {
        const safeMessage = getSafeErrorMessage(response);
        setError(safeMessage);

        logError('Failed to delete snapshot', {
          snapshotId,
          snapshotName,
          response: response || 'No response',
          duration: `${duration.toFixed(2)}ms`
        });

        logUserAction('delete_snapshot_failed', {
          snapshotId,
          snapshotName
        });
      }
    }
  } catch (err) {
    const endTime = performance.now();
    const duration = endTime - startTime;
    
    // Position 1: Authentication error
    if (err.status === 401 || err.code === 'AUTH_ERROR') {
      handleAuthError();
      setDeleteModal({ open: false, snapshot: null });
      setLoadingStates(prev => {
        const newDeleting = new Set(prev.deleting);
        newDeleting.delete(snapshotId);
        return { ...prev, deleting: newDeleting };
      });
      return;
    }
    
    // Position 2: Rate limit
    if (err.status === 429 || err.code === 'RATE_LIMIT_EXCEEDED') {
      setError(ERROR_MESSAGES['RATE_LIMIT_EXCEEDED']);
      logError('Delete snapshot rate limited', { 
        status: err.status,
        code: err.code,
        duration: `${duration.toFixed(2)}ms` 
      }, err);
      setDeleteModal({ open: false, snapshot: null });
      setLoadingStates(prev => {
        const newDeleting = new Set(prev.deleting);
        newDeleting.delete(snapshotId);
        return { ...prev, deleting: newDeleting };
      });
      return;
    }
    
    // Position 3: Server errors (500+)
    if (err.status >= 500 || err.code === 'SERVER_ERROR' || err.code === 'SERVICE_UNAVAILABLE') {
      setError(ERROR_MESSAGES['SERVICE_UNAVAILABLE']);
      logError('Delete snapshot server error', { 
        status: err.status,
        code: err.code,
        duration: `${duration.toFixed(2)}ms` 
      }, err);
      setDeleteModal({ open: false, snapshot: null });
      setLoadingStates(prev => {
        const newDeleting = new Set(prev.deleting);
        newDeleting.delete(snapshotId);
        return { ...prev, deleting: newDeleting };
      });
      return;
    }
    
    // Position 4: Network errors (no status means network issue)
    if (!err.status) {
      const errorMessage = ERROR_MESSAGES[err.code] || err.error || ERROR_MESSAGES['NETWORK_ERROR'];
      setError(errorMessage);
      logError('Network error in deleteSnapshot', { 
        code: err.code,
        online: navigator.onLine,
        duration: `${duration.toFixed(2)}ms`,
        snapshotId,
        snapshotName
      }, err);
      setDeleteModal({ open: false, snapshot: null });
      setLoadingStates(prev => {
        const newDeleting = new Set(prev.deleting);
        newDeleting.delete(snapshotId);
        return { ...prev, deleting: newDeleting };
      });
      return;
    }
    
    // Position 5: Known error codes
    if (err.code && ERROR_MESSAGES[err.code]) {
      setError(ERROR_MESSAGES[err.code]);
      logError('Delete snapshot error with code', {
        code: err.code,
        duration: `${duration.toFixed(2)}ms`
      }, err);
      setDeleteModal({ open: false, snapshot: null });
      setLoadingStates(prev => {
        const newDeleting = new Set(prev.deleting);
        newDeleting.delete(snapshotId);
        return { ...prev, deleting: newDeleting };
      });
      return;
    }
    
    // Position 6: Use err.error (user-friendly message from interceptor)
    if (err.error) {
      setError(err.error);
      logError('Delete snapshot error', {
        error: err.error,
        duration: `${duration.toFixed(2)}ms`
      }, err);
      setDeleteModal({ open: false, snapshot: null });
      setLoadingStates(prev => {
        const newDeleting = new Set(prev.deleting);
        newDeleting.delete(snapshotId);
        return { ...prev, deleting: newDeleting };
      });
      return;
    }
    
    // Position 7: Fallback
    const safeMessage = getSafeErrorMessage(err);
    setError(safeMessage);

    logError('Error deleting snapshot', {
      snapshotId,
      snapshotName,
      errorName: err.name,
      duration: `${duration.toFixed(2)}ms`
    }, err);

    logUserAction('delete_snapshot_error', {
      snapshotId,
      snapshotName
    });
  } finally {
    setDeleteModal({ open: false, snapshot: null });
    setLoadingStates(prev => {
      const newDeleting = new Set(prev.deleting);
      newDeleting.delete(snapshotId);
      return { ...prev, deleting: newDeleting };
    });
  }
}, [deleteModal.snapshot, debouncedFetchSnapshots, handleAuthError, notification]);


const handleRestoreClick = useCallback((snapshot) => {
    if (!snapshot) return;
    
    logUserAction('restore_snapshot_modal_opened', {
      snapshotId: snapshot.snapshot_id,
      snapshotName: snapshot.name,
      snapshotStatus: snapshot.status
    });

    logInfo('Opening restore snapshot modal', {
      snapshotId: snapshot.snapshot_id,
      snapshotName: snapshot.name,
      snapshotStatus: snapshot.status,
      vmId: snapshot.vm_id
    });
    
    setSelectedSnapshot(snapshot);
    setRestoreModalOpen(true);
  }, []);

const handleRestoreConfirm = useCallback(async (restoreData) => {
  if (!selectedSnapshot) return;
  
  const startTime = performance.now();
  const snapshotId = selectedSnapshot.snapshot_id;
  const snapshotName = selectedSnapshot.name;
  
  logUserAction('restore_snapshot_confirmed', {
    snapshotId,
    snapshotName,
    newVmName: restoreData.new_vm_name
  });

  logInfo('Starting snapshot restoration', {
    snapshotId,
    snapshotName,
    newVmName: restoreData.new_vm_name
  });

  const nameValidation = validateSnapshotName(restoreData.new_vm_name);
  if (!nameValidation.valid) {
    setError(nameValidation.error);
    logError('Snapshot restoration failed - invalid VM name', {
      snapshotId,
      snapshotName,
      newVmName: restoreData.new_vm_name,
      validationError: nameValidation.error
    });
    return;
  }
  
  try {
    setLoadingStates(prev => ({ 
      ...prev, 
      restoring: new Set([...prev.restoring, snapshotId])
    }));
    
    const sanitizedData = {
      ...restoreData,
      new_vm_name: sanitizeText(restoreData.new_vm_name)
    };
    
    const response = await api.restoreSnapshot(snapshotId, sanitizedData);
    const endTime = performance.now();
    const duration = endTime - startTime;
    
    if (response && validateErrorObject(response)) {
      if (response.success) {
        setRestoreModalOpen(false);
        notification.success('Snapshot restored successfully');
        debouncedFetchSnapshots();

        logPerformance('restore_snapshot', duration, {
          snapshotId,
          snapshotName,
          newVmName: sanitizedData.new_vm_name,
          success: true
        });

        logInfo('Snapshot restored successfully', {
          snapshotId,
          snapshotName,
          newVmName: sanitizedData.new_vm_name,
          newVmId: response.vm_id || 'pending',
          duration: `${duration.toFixed(2)}ms`
        });

        logUserAction('restore_snapshot_success', {
          snapshotId,
          snapshotName,
          newVmName: sanitizedData.new_vm_name,
          newVmId: response.vm_id || 'pending'
        });
      } else {
        const safeMessage = getSafeErrorMessage(response);
        setError(safeMessage);

        logError('Failed to restore snapshot', {
          snapshotId,
          snapshotName,
          newVmName: sanitizedData.new_vm_name,
          response: response || 'No response',
          duration: `${duration.toFixed(2)}ms`
        });

        logUserAction('restore_snapshot_failed', {
          snapshotId,
          snapshotName,
          newVmName: sanitizedData.new_vm_name
        });
      }
    }
  } catch (err) {
    const endTime = performance.now();
    const duration = endTime - startTime;
    
    // Position 1: Authentication error
    if (err.status === 401 || err.code === 'AUTH_ERROR') {
      handleAuthError();
      setLoadingStates(prev => {
        const newRestoring = new Set(prev.restoring);
        newRestoring.delete(snapshotId);
        return { ...prev, restoring: newRestoring };
      });
      return;
    }
    
    // Position 2: Rate limit
    if (err.status === 429 || err.code === 'RATE_LIMIT_EXCEEDED') {
      setError(ERROR_MESSAGES['RATE_LIMIT_EXCEEDED']);
      logError('Restore snapshot rate limited', { 
        status: err.status,
        code: err.code,
        duration: `${duration.toFixed(2)}ms` 
      }, err);
      setLoadingStates(prev => {
        const newRestoring = new Set(prev.restoring);
        newRestoring.delete(snapshotId);
        return { ...prev, restoring: newRestoring };
      });
      return;
    }
    
    // Position 3: Server errors (500+)
    if (err.status >= 500 || err.code === 'SERVER_ERROR' || err.code === 'SERVICE_UNAVAILABLE') {
      setError(ERROR_MESSAGES['SERVICE_UNAVAILABLE']);
      logError('Restore snapshot server error', { 
        status: err.status,
        code: err.code,
        duration: `${duration.toFixed(2)}ms` 
      }, err);
      setLoadingStates(prev => {
        const newRestoring = new Set(prev.restoring);
        newRestoring.delete(snapshotId);
        return { ...prev, restoring: newRestoring };
      });
      return;
    }
    
    // Position 4: Network errors (no status means network issue)
    if (!err.status) {
      const errorMessage = ERROR_MESSAGES[err.code] || err.error || ERROR_MESSAGES['NETWORK_ERROR'];
      setError(errorMessage);
      logError('Network error in restoreSnapshot', { 
        code: err.code,
        online: navigator.onLine,
        duration: `${duration.toFixed(2)}ms`,
        snapshotId,
        snapshotName,
        newVmName: restoreData.new_vm_name
      }, err);
      setLoadingStates(prev => {
        const newRestoring = new Set(prev.restoring);
        newRestoring.delete(snapshotId);
        return { ...prev, restoring: newRestoring };
      });
      return;
    }
    
    // Position 5: Known error codes
    if (err.code && ERROR_MESSAGES[err.code]) {
      setError(ERROR_MESSAGES[err.code]);
      logError('Restore snapshot error with code', {
        code: err.code,
        duration: `${duration.toFixed(2)}ms`
      }, err);
      setLoadingStates(prev => {
        const newRestoring = new Set(prev.restoring);
        newRestoring.delete(snapshotId);
        return { ...prev, restoring: newRestoring };
      });
      return;
    }
    
    // Position 6: Use err.error (user-friendly message from interceptor)
    if (err.error) {
      setError(err.error);
      logError('Restore snapshot error', {
        error: err.error,
        duration: `${duration.toFixed(2)}ms`
      }, err);
      setLoadingStates(prev => {
        const newRestoring = new Set(prev.restoring);
        newRestoring.delete(snapshotId);
        return { ...prev, restoring: newRestoring };
      });
      return;
    }
    
    // Position 7: Fallback
    const safeMessage = getSafeErrorMessage(err);
    setError(safeMessage);

    logError('Error restoring snapshot', {
      snapshotId,
      snapshotName,
      newVmName: restoreData.new_vm_name,
      errorName: err.name,
      duration: `${duration.toFixed(2)}ms`
    }, err);

    logUserAction('restore_snapshot_error', {
      snapshotId,
      snapshotName,
      newVmName: restoreData.new_vm_name
    });
  } finally {
    setLoadingStates(prev => {
      const newRestoring = new Set(prev.restoring);
      newRestoring.delete(snapshotId);
      return { ...prev, restoring: newRestoring };
    });
  }
}, [selectedSnapshot, debouncedFetchSnapshots, handleAuthError, notification]);

const handleRegionChange = useCallback((region) => {
    logUserAction('region_changed', {
      fromRegion: selectedRegion,
      toRegion: region
    });

    logInfo('Region changed', {
      fromRegion: selectedRegion,
      toRegion: region,
      currentSnapshotCount: snapshots.length,
      currentVMCount: vms.length
    });
    
    setSelectedRegion(region);
    setIsRegionDropdownOpen(false);
    setGroupPages({ 'In Progress': 1, Available: 1 });
    setExpandedSnapshotId(null);
    setSearchQuery('');
  }, [selectedRegion, snapshots.length, vms.length]);
  
  const toggleRegionDropdown = useCallback((e) => {
    e.stopPropagation();
    const newState = !isRegionDropdownOpen;
    
    logUserAction('region_dropdown_toggled', {
      opened: newState
    });
    
    setIsRegionDropdownOpen(newState);
  }, [isRegionDropdownOpen]);

  // Performance: Controlled polling with retry limits
  const maxRetries = 3;
  const retryAttempts = useRef(0);

  const handleRefresh = useCallback(async () => {
    if (retryAttempts.current >= maxRetries) {
      const errorMsg = 'Maximum refresh attempts reached. Please wait before trying again.';
      setError(errorMsg);
      logError('Maximum refresh attempts exceeded', {
        attempts: retryAttempts.current,
        maxRetries,
        selectedRegion
      });
      return;
    }
    
    retryAttempts.current++;
    
    logUserAction('refresh_snapshots', {
      attempt: retryAttempts.current,
      maxRetries,
      selectedRegion
    });

    logInfo('Refreshing snapshots and VMs', {
      attempt: retryAttempts.current,
      maxRetries,
      currentSnapshotCount: snapshots.length,
      currentVMCount: vms.length,
      selectedRegion
    });
    
    try {
      await fetchAllData();
      retryAttempts.current = 0;
      logInfo('Snapshot refresh completed successfully');
    } catch (err) {
      if (retryAttempts.current >= maxRetries) {
        const safeMessage = getSafeErrorMessage(err);
        setError(safeMessage);
        logError('Snapshot refresh failed after max retries', {
          attempts: retryAttempts.current,
          maxRetries,
          selectedRegion
        }, err);
      }
    }
  }, [fetchAllData, snapshots.length, vms.length, selectedRegion]);

  // Handle opening create modal
  const handleOpenCreateModal = useCallback(() => {
    logUserAction('create_snapshot_modal_opened');
    logInfo('Opening create snapshot modal', {
      availableVMs: filteredVms.length
    });
    setCreateModalOpen(true);
  }, [filteredVms.length]);

  // Toggle expanded snapshot row
  const handleToggleExpand = useCallback((snapshotId) => {
    setExpandedSnapshotId(prev => prev === snapshotId ? null : snapshotId);
    logUserAction('toggle_snapshot_expand', {
      snapshotId,
      expanded: expandedSnapshotId !== snapshotId
    });
  }, [expandedSnapshotId]);

  const formatDate = useCallback((dateString) => {
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString();
    } catch (e) {
      return 'Unknown date';
    }
  }, []);

  // Render snapshot row (collapsed state) - DESKTOP
  const renderSnapshotRow = useCallback((snapshot) => {
    const snapshotKey = snapshot.snapshot_id || snapshot.id;
    const isExpanded = expandedSnapshotId === snapshotKey;
    const isAvailable = snapshot.status === 'available' || snapshot.status === 'SUCCESS';

    return (
      <div key={snapshotKey}>
        {/* Collapsed Row */}
        <div 
          style={{
            display: 'flex',
            alignItems: 'center',
            padding: '10px 16px',
            borderBottom: '1px solid #222',
            borderLeft: '3px solid #6b7280',
            background: 'transparent',
            transition: 'background 0.2s'
          }}
          onMouseEnter={(e) => e.currentTarget.style.background = '#1a1a1a'}
          onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
        >
          <div 
            onClick={() => handleToggleExpand(snapshotKey)}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '16px',
              flex: 1,
              cursor: 'pointer',
              minWidth: 0
            }}
          >
            <span style={{ color: '#fff', fontSize: '14px', fontWeight: '500', minWidth: '140px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {sanitizeText(snapshot.name)}
            </span>
            <span style={{ color: '#9ca3af', fontSize: '12px' }}>
              {snapshot.size || 0} GB
            </span>
            <span style={{ color: '#6b7280', fontSize: '12px' }}>
              {sanitizeText(snapshot.region)}
            </span>
            
            {/* Status Badge */}
            <span style={{
              padding: '2px 8px',
              borderRadius: '4px',
              fontSize: '12px',
              fontWeight: '500',
              background: isAvailable ? 'rgba(202,217,238,0.15)' : 'rgba(202,217,238,0.1)',
              color: '#cad9ee',
              border: isAvailable ? '1px solid #cad9ee' : '1px solid rgba(202,217,238,0.4)',
              animation: !isAvailable ? 'pulse-creating 2s infinite' : 'none'
            }}>
              {(sanitizeText(snapshot.status) || 'unknown').toLowerCase()}
            </span>
            
            {/* VM Name */}
            <span style={{ color: '#60a5fa', fontSize: '12px' }}>
              → {sanitizeText(snapshot.vm_name) || 'Unknown VM'}
            </span>
          </div>
          
   
          
          {/* Restore Button */}
          <button
            onClick={() => handleRestoreClick(snapshot)}
            disabled={!isAvailable || loadingStates.restoring.has(snapshotKey)}
            style={{
              padding: '6px 12px',
              borderRadius: '4px',
              fontSize: '12px',
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              cursor: isAvailable && !loadingStates.restoring.has(snapshotKey) ? 'pointer' : 'not-allowed',
              border: 'none',
              background: '#e0eacc',
              color: '#000',
              opacity: isAvailable ? 1 : 0.5
            }}
          >
            {loadingStates.restoring.has(snapshotKey) ? (
              <>
                <div style={{
                  width: '12px',
                  height: '12px',
                  border: '2px solid transparent',
                  borderTop: '2px solid #000',
                  borderRadius: '50%',
                  animation: 'spin 1s linear infinite'
                }}></div>
                Restoring...
              </>
            ) : (
              <>
                <FaUndo size={10} /> Restore
              </>
            )}
          </button>
          
          {/* Expand chevron */}
          <div 
            onClick={() => handleToggleExpand(snapshotKey)}
            style={{ marginLeft: '12px', cursor: 'pointer', padding: '4px' }}
          >
            <FaChevronRight 
              size={10} 
              style={{
                color: '#6b7280',
                transition: 'transform 0.2s',
                transform: isExpanded ? 'rotate(90deg)' : 'rotate(0deg)'
              }}
            />
          </div>
        </div>
        
        {/* Expanded Details */}
        {isExpanded && (
          <div style={{
            padding: '16px',
            background: '#0d0d0d',
            borderBottom: '1px solid #222'
          }}>
            <div style={{
              display: 'grid',
              gridTemplateColumns: '1fr auto',
              gap: '16px'
            }}>
              {/* Details Grid */}
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(3, 1fr)',
                gap: '12px'
              }}>
                <div>
                  <span style={{ fontSize: '10px', color: '#6b7280', textTransform: 'uppercase', display: 'block', marginBottom: '4px' }}>Snapshot ID</span>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <span style={{ fontSize: '12px', color: '#fff' }}>{sanitizeText(snapshot.snapshot_id || snapshot.id)}</span>
                    <button 
                      onClick={() => copyToClipboard(snapshot.snapshot_id || snapshot.id)}
                      style={{ padding: '2px', background: 'transparent', border: 'none', cursor: 'pointer' }}
                    >
                      <FaCopy size={10} style={{ color: copiedId === (snapshot.snapshot_id || snapshot.id) ? '#a5c77f' : '#6b7280' }} />
                    </button>
                  </div>
                </div>
                <div>
                  <span style={{ fontSize: '10px', color: '#6b7280', textTransform: 'uppercase', display: 'block', marginBottom: '4px' }}>Created</span>
                  <span style={{ fontSize: '12px', color: '#fff' }}>{formatDate(snapshot.created_at)}</span>
                </div>
                <div>
                  <span style={{ fontSize: '10px', color: '#6b7280', textTransform: 'uppercase', display: 'block', marginBottom: '4px' }}>Is Image</span>
<span style={{ fontSize: '12px', color: '#fff' }}>{snapshotImageStatus.get(snapshot.snapshot_id)?.hasImage ? 'Yes' : 'No'}</span>                </div>
              </div>
              
              {/* Action Buttons */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                <button
                  onClick={() => handleDeleteClick(snapshot)}
                  disabled={loadingStates.deleting.has(snapshotKey)}
                  style={{
                    background: 'rgba(238,85,85,0.1)',
                    color: '#ff7777',
                    border: '1px solid #ff7777',
                    padding: '8px 14px',
                    borderRadius: '6px',
                    fontSize: '12px',
                    cursor: loadingStates.deleting.has(snapshotKey) ? 'not-allowed' : 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    justifyContent: 'center',
                    minWidth: '90px'
                  }}
                >
                  {loadingStates.deleting.has(snapshotKey) ? (
                    <>
                      <div style={{
                        width: '12px',
                        height: '12px',
                        border: '2px solid transparent',
                        borderTop: '2px solid #ff7777',
                        borderRadius: '50%',
                        animation: 'spin 1s linear infinite'
                      }}></div>
                      Deleting...
                    </>
                  ) : (
                    <>
                      <FaTrash size={10} /> Delete
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
 }, [
  expandedSnapshotId, 
  loadingStates, 
  copiedId, 
  handleToggleExpand, 
  handleRestoreClick, 
  handleDeleteClick, 
  copyToClipboard,
  formatDate,
  snapshotImageStatus
]);

  // Render snapshot card (for mobile hybrid layout)
  const renderSnapshotCard = useCallback((snapshot) => {
    const snapshotKey = snapshot.snapshot_id || snapshot.id;
    const isAvailable = snapshot.status === 'available' || snapshot.status === 'SUCCESS';

    return (
      <div 
        key={snapshotKey}
        style={{
          background: '#121212',
          borderRadius: '10px',
          border: '1px solid #333',
          padding: '16px',
          marginBottom: '12px'
        }}
      >
        {/* Card Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '12px' }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <h3 style={{ fontSize: '16px', fontWeight: '600', color: '#fff', margin: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {sanitizeText(snapshot.name)}
            </h3>
            <p style={{ fontSize: '12px', color: '#6b7280', margin: '4px 0 0 0' }}>
              {snapshot.size || 0} GB • {sanitizeText(snapshot.region)}
            </p>
          </div>
          <span style={{
            padding: '4px 8px',
            borderRadius: '4px',
            fontSize: '11px',
            fontWeight: '500',
            background: isAvailable ? 'rgba(202,217,238,0.15)' : 'rgba(202,217,238,0.1)',
            color: '#cad9ee',
            border: isAvailable ? '1px solid #cad9ee' : '1px solid rgba(202,217,238,0.4)',
            animation: !isAvailable ? 'pulse-creating 2s infinite' : 'none'
          }}>
            {(sanitizeText(snapshot.status) || 'unknown').toLowerCase()}
          </span>
        </div>
        
        {/* Card Details */}
        <div style={{ marginBottom: '12px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px' }}>
            <span style={{ fontSize: '12px', color: '#6b7280' }}>VM:</span>
            <span style={{ fontSize: '12px', color: '#60a5fa' }}>{sanitizeText(snapshot.vm_name) || 'Unknown'}</span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px' }}>
            <span style={{ fontSize: '12px', color: '#6b7280' }}>Created:</span>
            <span style={{ fontSize: '12px', color: '#fff' }}>{formatDate(snapshot.created_at)}</span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
            <span style={{ fontSize: '12px', color: '#6b7280' }}>Is Image:</span>
<span style={{ fontSize: '12px', color: '#fff' }}>{snapshotImageStatus.get(snapshot.snapshot_id)?.hasImage ? 'Yes' : 'No'}</span>          </div>
        </div>
        
        {/* Card Actions */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
          <button
            onClick={() => handleRestoreClick(snapshot)}
            disabled={!isAvailable || loadingStates.restoring.has(snapshotKey)}
            style={{
              padding: '10px',
              borderRadius: '6px',
              fontSize: '12px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '6px',
              cursor: isAvailable && !loadingStates.restoring.has(snapshotKey) ? 'pointer' : 'not-allowed',
              border: 'none',
              background: '#e0eacc',
              color: '#000',
              opacity: isAvailable ? 1 : 0.5
            }}
          >
            {loadingStates.restoring.has(snapshotKey) ? (
              <>
                <div style={{
                  width: '12px',
                  height: '12px',
                  border: '2px solid transparent',
                  borderTop: '2px solid #000',
                  borderRadius: '50%',
                  animation: 'spin 1s linear infinite'
                }}></div>
              </>
            ) : (
              <>
                <FaUndo size={12} /> Restore
              </>
            )}
          </button>
          
          <button
            onClick={() => handleDeleteClick(snapshot)}
            disabled={loadingStates.deleting.has(snapshotKey)}
            style={{
              padding: '10px',
              borderRadius: '6px',
              fontSize: '12px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '6px',
              cursor: loadingStates.deleting.has(snapshotKey) ? 'not-allowed' : 'pointer',
              border: '1px solid #ff7777',
              background: 'rgba(238,85,85,0.1)',
              color: '#ff7777'
            }}
          >
            {loadingStates.deleting.has(snapshotKey) ? (
              <div style={{
                width: '12px',
                height: '12px',
                border: '2px solid transparent',
                borderTop: '2px solid #ff7777',
                borderRadius: '50%',
                animation: 'spin 1s linear infinite'
              }}></div>
            ) : (
              <>
                <FaTrash size={12} /> Delete
              </>
            )}
          </button>
        </div>
      </div>
    );
}, [loadingStates, handleRestoreClick, handleDeleteClick, formatDate, snapshotImageStatus]);
  // Render snapshot group
  const renderSnapshotGroup = useCallback((groupName, groupSnapshots, isMobileView = false) => {
    if (groupSnapshots.length === 0) return null;
    
    const isCollapsed = collapsedGroups[groupName];
    const currentPage = groupPages[groupName] || 1;
    const totalPages = getGroupTotalPages(groupName);
    const paginatedSnapshots = getPaginatedGroupSnapshots(groupName);
    const totalSize = groupSnapshots.reduce((sum, s) => sum + (s.size || 0), 0);
    
    return (
      <div key={groupName}>
        {/* Group Header */}
        <div 
          onClick={() => toggleGroup(groupName)}
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '12px 16px',
            background: '#1a1a1a',
            borderBottom: '1px solid #333',
            cursor: 'pointer'
          }}
        >
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '10px',
            color: '#e0eacc',
            fontSize: '14px',
            fontWeight: '600'
          }}>
            <FaChevronRight 
              size={10} 
              style={{
                transition: 'transform 0.2s',
                transform: isCollapsed ? 'rotate(0deg)' : 'rotate(90deg)'
              }}
            />
            {groupName}
            <span style={{
              background: '#333',
              padding: '2px 8px',
              borderRadius: '12px',
              fontSize: '12px',
              color: '#9ca3af',
              fontWeight: '400'
            }}>
              {groupSnapshots.length}
            </span>
            {groupSnapshots.length > GROUP_ITEMS_PER_PAGE && (
              <span style={{
                background: 'rgba(224,234,204,0.1)',
                padding: '2px 8px',
                borderRadius: '12px',
                fontSize: '10px',
                color: '#e0eacc'
              }}>
                {totalPages} pages
              </span>
            )}
          </div>
          <div style={{ fontSize: '12px', color: '#6b7280' }}>
            {totalSize} GB total
          </div>
        </div>
        
        {/* Group Content */}
        {!isCollapsed && (
          <>
            {isMobileView ? (
              <div style={{ padding: '12px' }}>
                {paginatedSnapshots.map(renderSnapshotCard)}
              </div>
            ) : (
              paginatedSnapshots.map(renderSnapshotRow)
            )}
            
            {/* Group Pagination */}
            <GroupPagination
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={(page) => handleGroupPageChange(groupName, page)}
              totalItems={groupSnapshots.length}
            />
          </>
        )}
      </div>
    );
  }, [
    collapsedGroups, 
    groupPages, 
    getGroupTotalPages, 
    getPaginatedGroupSnapshots, 
    toggleGroup, 
    renderSnapshotRow,
    renderSnapshotCard,
    handleGroupPageChange
  ]);

  // CSS for animations
  const animationStyles = `
    @keyframes spin {
      0% { transform: rotate(0deg); }
      100% { transform: rotate(360deg); }
    }
    
    @keyframes pulse-creating {
      0% {
        background: rgba(202, 217, 238, 0.1);
        border-color: rgba(202, 217, 238, 0.4);
        box-shadow: 0 0 0 0 rgba(202, 217, 238, 0.4);
      }
      50% {
        background: rgba(202, 217, 238, 0.2);
        border-color: rgba(202, 217, 238, 0.6);
        box-shadow: 0 0 0 4px rgba(202, 217, 238, 0.1);
      }
      100% {
        background: rgba(202, 217, 238, 0.1);
        border-color: rgba(202, 217, 238, 0.4);
        box-shadow: 0 0 0 0 rgba(202, 217, 238, 0);
      }
    }
  `;

  // MOBILE/TABLET LAYOUT (Hybrid: Grouped headers + Cards)
  if (isMobile || isTablet) {
    return (
      <div style={{ display: 'flex', background: '#000', minHeight: '100vh', color: '#fff' }}>
        <style>{animationStyles}</style>
        <div style={{ flex: 1, padding: isMobile ? '16px' : '24px' }}>
          <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
            {/* Header Section */}
            <div style={{
              marginBottom: '32px',
              background: '#121212',
              border: '1px solid #333333',
              borderRadius: '12px',
              boxShadow: '0 4px 6px rgba(0,0,0,0.3)',
              padding: isMobile ? '24px' : '32px'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', marginBottom: isMobile ? '24px' : '0' }}>
                <div style={{
                  background: '#222222',
                  border: '1px solid #333333',
                  borderRadius: '50%',
                  boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
                  marginRight: '24px',
                  padding: isMobile ? '12px' : '16px'
                }}>
<Camera style={{ color: '#e0eacc' }} size={isMobile ? 16 : 20} strokeWidth={1.5} />
                </div>
                <div>
                  <h1 style={{ fontWeight: '500', color: '#fff', fontSize: isMobile ? '24px' : '30px', margin: 0 }}>Snapshots</h1>
                  <p style={{ color: '#9ca3af', marginTop: '8px', fontSize: isMobile ? '14px' : '16px', margin: '8px 0 0 0' }}>
                    Create and restore point-in-time copies of your virtual machines
                  </p>
                </div>
              </div>

              {/* Mobile Actions */}
              <div style={{ marginTop: '24px' }}>
                <div style={{ display: 'flex', gap: '12px', marginBottom: '12px' }}>
                  <button
                    onClick={handleOpenCreateModal}
                    disabled={filteredVms.length === 0 || !isProfileComplete || loadingStates.creating}
                    style={{
                      flex: 1,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      padding: '12px 16px',
                      borderRadius: '6px',
                      fontWeight: '500',
                      border: 'none',
                      cursor: (filteredVms.length === 0 || !isProfileComplete) ? 'not-allowed' : 'pointer',
                      background: (filteredVms.length === 0 || !isProfileComplete) ? '#666' : '#e0eacc',
                      color: (filteredVms.length === 0 || !isProfileComplete) ? '#999' : '#000'
                    }}
                  >
                    <FaPlus style={{ marginRight: '8px' }} size={14} /> Create Snapshot
                  </button>
                  
                  <button
                    onClick={handleRefresh}
                    disabled={loadingStates.snapshots || loadingStates.vms}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      padding: '10px 12px',
                      background: '#333',
                      borderRadius: '6px',
                      fontSize: '14px',
                      border: 'none',
                      cursor: 'pointer',
                      color: '#fff',
                      position: 'relative',
                      overflow: 'hidden'
                    }}
                  >
                    <div style={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      height: '100%',
                      background: 'linear-gradient(to right, #cad9e3, #b8c7d1)',
                      transition: 'width 0.3s',
                      borderRadius: '6px',
                      width: (loadingStates.snapshots || loadingStates.vms) ? '100%' : '0%'
                    }}></div>
                    <div style={{ position: 'relative', zIndex: 10, display: 'flex', alignItems: 'center' }}>
                      <svg 
                        style={{
                          marginRight: '8px',
                          width: '16px',
                          height: '16px',
                          animation: (loadingStates.snapshots || loadingStates.vms) ? 'spin 1s linear infinite' : 'none'
                        }}
                        xmlns="http://www.w3.org/2000/svg" 
                        fill="none" 
                        viewBox="0 0 24 24" 
                        stroke="currentColor"
                      >
                        <path 
                          strokeLinecap="round" 
                          strokeLinejoin="round" 
                          strokeWidth="2" 
                          d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" 
                        />
                      </svg>
                      <span style={{ color: (loadingStates.snapshots || loadingStates.vms) ? 'black' : 'white' }}>
                        Refresh
                      </span>
                    </div>
                  </button>
                </div>
                
                {/* Region Dropdown */}
                <div style={{ position: 'relative' }} ref={regionDropdownRef}>
                  <div 
                    onClick={toggleRegionDropdown}
                    style={{
                      width: '100%',
                      padding: '12px 16px',
                      background: '#1d1d1d',
                      border: '1px solid #333333',
                      borderRadius: '6px',
                      color: '#fff',
                      fontSize: '14px',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      cursor: 'pointer'
                    }}
                  >
                    <span>Region: {selectedRegion}</span>
                    <FaChevronDown size={12} style={{ color: '#6b7280' }} />
                  </div>
                  
                  {isRegionDropdownOpen && (
                    <div style={{
                      position: 'absolute',
                      zIndex: 50,
                      left: 0,
                      right: 0,
                      marginTop: '4px',
                      background: '#1d1d1d',
                      border: '1px solid #333333',
                      borderRadius: '6px',
                      boxShadow: '0 4px 6px rgba(0,0,0,0.3)',
                      overflow: 'hidden'
                    }}>
                      <div style={{ padding: '4px' }}>
                        {regions.map((region) => (
                          <div 
                            key={region} 
                            onClick={() => handleRegionChange(region)}
                            style={{
                              margin: '4px',
                              padding: '10px 12px',
                              cursor: 'pointer',
                              borderRadius: '4px',
                              background: region === selectedRegion ? '#252525' : 'transparent',
                              color: region === selectedRegion ? '#fff' : '#d1d5db',
                              border: region === selectedRegion ? '1px solid #cad9e3' : '1px solid transparent'
                            }}
                          >
                            {region}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Search and Sort */}
            <div style={{ display: 'flex', gap: '12px', alignItems: 'center', marginBottom: '16px' }}>
              <div style={{
                flex: 1,
                background: '#1a1a1a',
                border: '1px solid #333',
                borderRadius: '6px',
                padding: '8px 12px',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}>
                <FaSearch size={14} style={{ color: '#6b7280' }} />
                <input
                  type="text"
                  placeholder="Search snapshots..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  maxLength={30}
                  style={{
                    background: 'transparent',
                    border: 'none',
                    outline: 'none',
                    color: '#fff',
                    flex: 1,
                    fontSize: '14px'
                  }}
                />
                {searchQuery && (
                  <button 
                    onClick={() => setSearchQuery('')}
                    style={{ background: 'transparent', border: 'none', cursor: 'pointer', padding: 0 }}
                  >
                    <FaTimes size={12} style={{ color: '#6b7280' }} />
                  </button>
                )}
              </div>
              
              <div style={{ position: 'relative' }}>
                <button
                  onClick={() => setSortDropdownOpen(!sortDropdownOpen)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                    padding: '8px 12px',
                    background: '#1a1a1a',
                    border: '1px solid #333',
                    borderRadius: '6px',
                    color: '#9ca3af',
                    fontSize: '12px',
                    cursor: 'pointer'
                  }}
                >
                  <FaSort size={12} />
                  {sortOptions.find(s => s.id === sortBy)?.label}
                </button>
                {sortDropdownOpen && (
                  <div style={{
                    position: 'absolute',
                    top: '100%',
                    right: 0,
                    marginTop: '4px',
                    background: '#1a1a1a',
                    border: '1px solid #333',
                    borderRadius: '6px',
                    overflow: 'hidden',
                    zIndex: 50,
                    minWidth: '100px'
                  }}>
                    {sortOptions.map(s => (
                      <div
                        key={s.id}
                        onClick={() => { setSortBy(s.id); setSortDropdownOpen(false); }}
                        style={{
                          padding: '8px 12px',
                          fontSize: '12px',
                          cursor: 'pointer',
                          color: sortBy === s.id ? '#e0eacc' : '#9ca3af',
                          background: sortBy === s.id ? 'rgba(224,234,204,0.1)' : 'transparent'
                        }}
                      >
                        {s.label}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
            
            <div style={{ marginLeft: 'auto', color: '#6b7280', fontSize: '12px', marginBottom: '16px' }}>
              {filteredSnapshots.length} snapshot{filteredSnapshots.length !== 1 ? 's' : ''}
            </div>

            {/* Error Display */}
            {error && (
              <div style={{
                marginBottom: '24px',
                background: 'rgba(239,68,68,0.1)',
                border: '1px solid #991b1b',
                borderRadius: '6px',
                padding: '16px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between'
              }}>
                <p style={{ color: '#f87171', fontSize: '14px', margin: 0 }}>{error}</p>
                <button
                  onClick={() => {
                    clearError();
                    handleRefresh();
                  }}
                  style={{
                    marginLeft: '16px',
                    padding: '4px 12px',
                    background: '#b91c1c',
                    color: '#fff',
                    fontSize: '12px',
                    borderRadius: '4px',
                    border: 'none',
                    cursor: 'pointer',
                    flexShrink: 0
                  }}
                >
                  Retry
                </button>
              </div>
            )}

            {/* Loading State */}
            {loadingStates.snapshots ? (
              <div style={{
                background: '#1a1a1a',
                border: '1px solid #333333',
                borderRadius: '12px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: '32px'
              }}>
                <div style={{
                  width: '32px',
                  height: '32px',
                  border: '4px solid rgba(224,234,204,0.2)',
                  borderTop: '4px solid #e0eacc',
                  borderRadius: '50%',
                  animation: 'spin 1s linear infinite'
                }}></div>
                <p style={{ color: '#9ca3af', marginLeft: '16px', fontSize: '14px' }}>Loading snapshots...</p>
              </div>
            ) : filteredSnapshots.length === 0 ? (
              /* Empty State */
              <div style={{
                background: '#121212',
                borderRadius: '12px',
                border: '1px solid #333333',
                padding: '24px'
              }}>
                <div style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  padding: '70px 0'
                }}>
                  <div style={{ marginBottom: '24px' }}>
<Camera style={{ color: '#e0eacc' }} size={40} strokeWidth={1.5} />
                  </div>
                  <h3 style={{ fontSize: '18px', fontWeight: '600', color: '#fff', marginBottom: '8px' }}>No Snapshots Found</h3>
                  <p style={{ color: '#9ca3af', textAlign: 'center', maxWidth: '400px', fontSize: '14px' }}>
                    {searchQuery 
                      ? `No snapshots matching "${searchQuery}" in ${selectedRegion}`
                      : `You haven't created any snapshots in ${selectedRegion} yet.`
                    }
                  </p>
                </div>
              </div>
            ) : (
              /* Snapshot Groups List */
              <div style={{
                background: '#121212',
                borderRadius: '10px',
                border: '1px solid #333',
                overflow: 'hidden'
              }}>
                {renderSnapshotGroup('In Progress', groupedSnapshots['In Progress'], true)}
                {renderSnapshotGroup('Available', groupedSnapshots.Available, true)}
              </div>
            )}
          </div>
        </div>

        {/* Modals */}
        <CreateSnapshotModal
          isOpen={createModalOpen}
          onClose={() => {
            setCreateModalOpen(false);
            logUserAction('create_snapshot_modal_closed');
          }}
          onSubmit={handleCreateSnapshot}
          vms={filteredVms}
          existingSnapshotNames={snapshots.map(snapshot => snapshot.name)}
        />

        <RestoreSnapshotModal
          isOpen={restoreModalOpen}
          onClose={() => {
            setRestoreModalOpen(false);
            logUserAction('restore_snapshot_modal_closed');
          }}
          onSubmit={handleRestoreConfirm}
          snapshot={selectedSnapshot}
        />
        
        <DeleteConfirmationModal
          isOpen={deleteModal.open}
          onClose={() => {
            setDeleteModal({ open: false, snapshot: null });
            logUserAction('delete_snapshot_modal_closed');
          }}
          onConfirm={handleDeleteConfirm}
          itemName={deleteModal.snapshot ? deleteModal.snapshot.name : ''}
          itemType="snapshot"
          isDeleting={deleteModal.snapshot ? loadingStates.deleting.has(deleteModal.snapshot.snapshot_id) : false}
        />
        
      </div>
    );
  }

  // DESKTOP LAYOUT
  return (
    <div style={{ display: 'flex', background: '#000', minHeight: '100vh', color: '#fff' }}>
      <style>{animationStyles}</style>
      
      <Sidebar 
        activePage="snapshots" 
        isMobile={isMobile}
        isTablet={isTablet}
        isSidebarOpen={false}
        toggleSidebar={() => {}}
        authenticated={true}
        onAuthAction={() => {}}
      />
      
      <div style={{ flex: 1, overflowX: 'hidden' }}>
        <div style={{ width: '100%', maxWidth: '1600px', margin: '0 auto', padding: '0 32px' }}>
          {/* Header */}
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '24px 0',
            borderBottom: '1px solid #333333'
          }}>
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <div style={{ marginRight: '16px' }}>
<Camera style={{ color: '#e0eacc' }} size={20} strokeWidth={1.5} />
              </div>
              <div>
                <h1 style={{ fontSize: '24px', fontWeight: '700', color: '#fff', margin: 0 }}>Snapshots</h1>
                <p style={{ color: '#9ca3af', fontSize: '14px', margin: '4px 0 0 0' }}>Create and restore point-in-time copies of your virtual machines</p>
              </div>
            </div>
            
            <div style={{ display: 'flex', gap: '12px' }}>
              <button
                onClick={handleOpenCreateModal}
                disabled={filteredVms.length === 0 || !isProfileComplete || loadingStates.creating}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  padding: '10px 16px',
                  borderRadius: '6px',
                  fontWeight: '500',
                  border: 'none',
                  cursor: (filteredVms.length === 0 || !isProfileComplete) ? 'not-allowed' : 'pointer',
                  background: (filteredVms.length === 0 || !isProfileComplete) ? '#666' : '#e0eacc',
                  color: (filteredVms.length === 0 || !isProfileComplete) ? '#999' : '#000'
                }}
                title={
                  filteredVms.length === 0 
                    ? "No active VMs available for snapshot creation" 
                    : !isProfileComplete 
                      ? "Please complete your profile before creating snapshots"
                      : "Create a new snapshot"
                }
              >
                <FaPlus style={{ marginRight: '8px' }} /> Create Snapshot
              </button>
              
              {/* Region Dropdown */}
              <div style={{ position: 'relative' }} ref={regionDropdownRef}>
                <div 
                  onClick={toggleRegionDropdown}
                  style={{
                    width: '144px',
                    padding: '12px',
                    background: '#1d1d1d',
                    border: '1px solid #333333',
                    borderRadius: '6px',
                    color: '#fff',
                    fontSize: '14px',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    cursor: 'pointer',
                    transition: 'border-color 0.15s'
                  }}
                >
                  <span>{selectedRegion}</span>
                  <div style={{ display: 'flex', gap: '2px' }}>
                    {[0, 1, 2].map((i) => (
                      <div 
                        key={i}
                        style={{
                          width: '4px',
                          height: '4px',
                          background: '#cad9e3',
                          borderRadius: '50%',
                          animation: isRegionDropdownOpen ? `bounce 0.6s ease ${i * 0.1}s infinite` : 'none'
                        }}
                      />
                    ))}
                  </div>
                </div>
                
                {isRegionDropdownOpen && (
                  <div style={{
                    position: 'absolute',
                    zIndex: 50,
                    left: 0,
                    right: 0,
                    marginTop: '4px',
                    background: '#1d1d1d',
                    border: '1px solid #333333',
                    borderRadius: '6px',
                    boxShadow: '0 4px 6px rgba(0,0,0,0.3)',
                    overflow: 'hidden'
                  }}>
                    <div style={{ padding: '4px' }}>
                      {regions.map((region) => (
                        <div 
                          key={region} 
                          onClick={() => handleRegionChange(region)}
                          style={{
                            margin: '4px 8px',
                            padding: '6px 8px',
                            cursor: 'pointer',
                            borderRadius: '4px',
                            background: region === selectedRegion ? '#252525' : 'transparent',
                            color: region === selectedRegion ? '#fff' : '#d1d5db',
                            border: region === selectedRegion ? '1px solid #cad9e3' : '1px solid transparent',
                            transition: 'all 0.2s'
                          }}
                        >
                          {region}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
              
              <button
                onClick={handleRefresh}
                disabled={loadingStates.snapshots || loadingStates.vms}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  padding: '12px 16px',
                  background: '#333',
                  borderRadius: '6px',
                  fontSize: '14px',
                  border: 'none',
                  cursor: 'pointer',
                  color: '#fff',
                  position: 'relative',
                  overflow: 'hidden'
                }}
              >
                <div style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  height: '100%',
                  background: 'linear-gradient(to right, #cad9e3, #b8c7d1)',
                  transition: 'width 0.3s',
                  borderRadius: '6px',
                  width: (loadingStates.snapshots || loadingStates.vms) ? '100%' : '0%'
                }}></div>
                <div style={{ position: 'relative', zIndex: 10, display: 'flex', alignItems: 'center' }}>
                  <svg 
                    style={{
                      marginRight: '8px',
                      width: '16px',
                      height: '16px',
                      animation: (loadingStates.snapshots || loadingStates.vms) ? 'spin 1s linear infinite' : 'none'
                    }}
                    xmlns="http://www.w3.org/2000/svg" 
                    fill="none" 
                    viewBox="0 0 24 24" 
                    stroke="currentColor"
                  >
                    <path 
                      strokeLinecap="round" 
                      strokeLinejoin="round" 
                      strokeWidth="2" 
                      d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" 
                    />
                  </svg>
                  <span style={{ color: (loadingStates.snapshots || loadingStates.vms) ? 'black' : 'white' }}>
                    Refresh
                  </span>
                </div>
              </button>
            </div>
          </div>
          
          <div style={{ padding: '24px 0', minHeight: '120vh' }}>
            {/* Error Display */}
            {error && (
              <div style={{
                marginBottom: '24px',
                background: 'rgba(239,68,68,0.1)',
                border: '1px solid #991b1b',
                borderRadius: '6px',
                padding: '16px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between'
              }}>
                <p style={{ color: '#f87171', fontSize: '14px', margin: 0 }}>{error}</p>
                <button
                  onClick={() => {
                    clearError();
                    handleRefresh();
                  }}
                  style={{
                    marginLeft: '16px',
                    padding: '4px 12px',
                    background: '#b91c1c',
                    color: '#fff',
                    fontSize: '12px',
                    borderRadius: '4px',
                    border: 'none',
                    cursor: 'pointer',
                    flexShrink: 0
                  }}
                >
                  Retry
                </button>
              </div>
            )}

            {/* Search and Sort Bar */}
            <div style={{ display: 'flex', gap: '12px', alignItems: 'center', marginBottom: '16px' }}>
              <div style={{
                flex: 1,
                maxWidth: '300px',
                background: '#1a1a1a',
                border: '1px solid #333',
                borderRadius: '6px',
                padding: '8px 12px',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}>
                <FaSearch size={14} style={{ color: '#6b7280' }} />
                <input
                  type="text"
                  placeholder="Search snapshots..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  maxLength={30}
                  style={{
                    background: 'transparent',
                    border: 'none',
                    outline: 'none',
                    color: '#fff',
                    flex: 1,
                    fontSize: '14px'
                  }}
                />
                {searchQuery && (
                  <button 
                    onClick={() => setSearchQuery('')}
                    style={{ background: 'transparent', border: 'none', cursor: 'pointer', padding: 0 }}
                  >
                    <FaTimes size={12} style={{ color: '#6b7280' }} />
                  </button>
                )}
              </div>
              
              <div style={{ position: 'relative' }}>
                <button
                  onClick={() => setSortDropdownOpen(!sortDropdownOpen)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                    padding: '8px 12px',
                    background: '#1a1a1a',
                    border: '1px solid #333',
                    borderRadius: '6px',
                    color: '#9ca3af',
                    fontSize: '12px',
                    cursor: 'pointer'
                  }}
                >
                  <FaSort size={12} />
                  {sortOptions.find(s => s.id === sortBy)?.label}
                </button>
                {sortDropdownOpen && (
                  <div style={{
                    position: 'absolute',
                    top: '100%',
                    left: 0,
                    marginTop: '4px',
                    background: '#1a1a1a',
                    border: '1px solid #333',
                    borderRadius: '6px',
                    overflow: 'hidden',
                    zIndex: 50,
                    minWidth: '100px'
                  }}>
                    {sortOptions.map(s => (
                      <div
                        key={s.id}
                        onClick={() => { setSortBy(s.id); setSortDropdownOpen(false); }}
                        style={{
                          padding: '8px 12px',
                          fontSize: '12px',
                          cursor: 'pointer',
                          color: sortBy === s.id ? '#e0eacc' : '#9ca3af',
                          background: sortBy === s.id ? 'rgba(224,234,204,0.1)' : 'transparent'
                        }}
                      >
                        {s.label}
                      </div>
                    ))}
                  </div>
                )}
              </div>
              
              <div style={{ marginLeft: 'auto', color: '#6b7280', fontSize: '12px' }}>
                {filteredSnapshots.length} snapshot{filteredSnapshots.length !== 1 ? 's' : ''}
              </div>
            </div>
            
            {/* Loading State */}
            {loadingStates.snapshots ? (
              <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: '64px 0'
              }}>
                <div style={{
                  width: '48px',
                  height: '48px',
                  border: '4px solid rgba(224,234,204,0.2)',
                  borderTop: '4px solid #e0eacc',
                  borderRadius: '50%',
                  animation: 'spin 1s linear infinite'
                }}></div>
                <p style={{ color: '#9ca3af', marginLeft: '16px' }}>Loading snapshots...</p>
              </div>
            ) : filteredSnapshots.length === 0 ? (
              /* Empty State */
              <div style={{
                background: '#121212',
                borderRadius: '10px',
                padding: '32px',
                border: '1px solid #333333',
                boxShadow: '0 6px 24px rgba(0,0,0,0.4)'
              }}>
                <div style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  padding: '90px 0'
                }}>
                  <div style={{ marginBottom: '24px' }}>
<Camera style={{ color: '#e0eacc' }} size={48} strokeWidth={1.5} />                  </div>
                  <h3 style={{ fontSize: '20px', fontWeight: '600', color: '#fff', marginBottom: '8px' }}>No Snapshots Found</h3>
                  <p style={{ color: '#9ca3af', textAlign: 'center', maxWidth: '400px' }}>
                    {searchQuery 
                      ? `No snapshots matching "${searchQuery}" in ${selectedRegion}`
                      : `You haven't created any snapshots in the ${selectedRegion} region yet.`
                    }
                  </p>
                </div>
              </div>
            ) : (
              /* Snapshot Groups List */
              <div style={{
                background: '#121212',
                borderRadius: '10px',
                border: '1px solid #333',
                overflow: 'hidden'
              }}>
                {renderSnapshotGroup('In Progress', groupedSnapshots['In Progress'], false)}
                {renderSnapshotGroup('Available', groupedSnapshots.Available, false)}
              </div>
            )}
          </div>
        </div>
      </div>
      
      {/* Modals */}
      <CreateSnapshotModal
        isOpen={createModalOpen}
        onClose={() => {
          setCreateModalOpen(false);
          logUserAction('create_snapshot_modal_closed');
        }}
        onSubmit={handleCreateSnapshot}
        vms={filteredVms}
        existingSnapshotNames={snapshots.map(snapshot => snapshot.name)}
      />

      <RestoreSnapshotModal
        isOpen={restoreModalOpen}
        onClose={() => {
          setRestoreModalOpen(false);
          logUserAction('restore_snapshot_modal_closed');
        }}
        onSubmit={handleRestoreConfirm}
        snapshot={selectedSnapshot}
      />
      
      <DeleteConfirmationModal
        isOpen={deleteModal.open}
        onClose={() => {
          setDeleteModal({ open: false, snapshot: null });
          logUserAction('delete_snapshot_modal_closed');
        }}
        onConfirm={handleDeleteConfirm}
        itemName={deleteModal.snapshot ? deleteModal.snapshot.name : ''}
        itemType="snapshot"
        isDeleting={deleteModal.snapshot ? loadingStates.deleting.has(deleteModal.snapshot.snapshot_id) : false}
      />
      
    </div>
  );
};

export default SnapshotPage;
